import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { Subject, takeUntil, combineLatest } from 'rxjs';
import { EpisodeService, SettingsService } from '../../core/services';
import { EpisodeListItem, PaginatedApiResponse } from '../../core/models';
import { environment } from '../../../environments/environment';

type ListType = 'all' | 'category' | 'tag';

@Component({
  selector: 'app-episode-list',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './episode-list.component.html',
  styleUrl: './episode-list.component.scss'
})
export class EpisodeListComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  episodes: EpisodeListItem[] = [];
  listType: ListType = 'all';
  slug?: string;
  pageTitle = 'Tutti gli episodi';
  
  // Paginazione
  currentPage = 1;
  totalPages = 1;
  totalItems = 0;
  itemsPerPage = 12;
  hasNextPage = false;
  hasPreviousPage = false;
  
  // Stati
  isLoading = false;
  error: string | null = null;
  defaultCoverUrl: string | null = null;

  constructor(
    private episodeService: EpisodeService,
    private settingsService: SettingsService,
    private route: ActivatedRoute,
    private router: Router,
    private meta: Meta,
    private title: Title
  ) {}

  ngOnInit(): void {
    console.log('🔵 ngOnInit called');
    
    // Carica prima le impostazioni per la copertina di default
    this.settingsService.getPublicSettings().subscribe({
      next: (response) => {
        if (response.data.default_cover) {
          const baseUrl = environment.apiUrl.replace('/api', '');
          const coverPath = response.data.default_cover;
          if (coverPath.startsWith('/')) {
            this.defaultCoverUrl = `${baseUrl}${coverPath}`;
          } else {
            this.defaultCoverUrl = `${baseUrl}/media/covers/${coverPath}`;
          }
        }
      },
      error: (err) => console.error('Error loading settings:', err)
    });

    // Combina tutti gli observable per evitare chiamate multiple
    combineLatest([
      this.route.data,
      this.route.params,
      this.route.queryParams
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([data, params, queryParams]) => {
        console.log('🟡 combineLatest triggered', { data, params, queryParams, currentPage: this.currentPage, episodesLength: this.episodes.length });
        
        // Aggiorna il tipo di lista
        this.listType = data['type'] || 'all';
        
        // Aggiorna lo slug
        this.slug = params['slug'];
        
        // Aggiorna il titolo
        this.updateTitle();
        
        // Aggiorna la pagina corrente
        const newPage = parseInt(queryParams['page']) || 1;
        
        console.log('🟢 Checking if should load:', { newPage, currentPage: this.currentPage, episodesLength: this.episodes.length });
        
        // Carica gli episodi SOLO se qualcosa è cambiato
        if (this.currentPage !== newPage || this.episodes.length === 0) {
          console.log('✅ Loading episodes...');
          this.currentPage = newPage;
          this.loadEpisodes();
        } else {
          console.log('⏭️ Skipping load - nothing changed');
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateTitle(): void {
    let pageTitle: string;
    let description: string;
    
    switch (this.listType) {
      case 'category':
        const categoryName = this.slug ? this.formatSlug(this.slug) : 'Categoria';
        pageTitle = `${categoryName} - AI PodLab`;
        description = `Esplora gli episodi nella categoria ${categoryName} del podcast AI PodLab su scienza e tecnologia.`;
        this.pageTitle = `Categoria: ${categoryName}`;
        break;
      case 'tag':
        const tagName = this.slug ? this.formatSlug(this.slug) : 'Tag';
        pageTitle = `${tagName} - AI PodLab`;
        description = `Episodi con il tag ${tagName} del podcast AI PodLab su scienza e tecnologia.`;
        this.pageTitle = `Tag: ${tagName}`;
        break;
      default:
        pageTitle = 'Tutti gli episodi - AI PodLab';
        description = 'Scopri tutti gli episodi del podcast AI PodLab su scienza, tecnologia e intelligenza artificiale.';
        this.pageTitle = 'Tutti gli episodi';
    }
    
    // Aggiorna i meta tag
    this.title.setTitle(pageTitle);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
  }

  private formatSlug(slug: string): string {
    return slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  loadEpisodes(): void {
    console.log('🟣 loadEpisodes called', { listType: this.listType, slug: this.slug, currentPage: this.currentPage });
    
    this.isLoading = true;
    this.error = null;

    let request$;

    switch (this.listType) {
      case 'category':
        if (!this.slug) {
          this.error = 'Categoria non specificata';
          this.isLoading = false;
          return;
        }
        request$ = this.episodeService.getByCategory(this.slug, this.currentPage, this.itemsPerPage);
        break;
      
      case 'tag':
        if (!this.slug) {
          this.error = 'Tag non specificato';
          this.isLoading = false;
          return;
        }
        request$ = this.episodeService.getByTag(this.slug, this.currentPage, this.itemsPerPage);
        break;
      
      default:
        request$ = this.episodeService.getAll(this.currentPage, this.itemsPerPage);
    }

    request$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: PaginatedApiResponse<any>) => {
          console.log('💚 API Response received:', response);
          // Mappa i dati dal formato backend al formato frontend
          this.episodes = this.mapEpisodesFromBackend(response.data);
          
          // L'API ritorna 'pagination' invece di 'meta'
          const paginationData = (response as any).pagination || response.meta;
          
          this.totalPages = paginationData.totalPages;
          this.totalItems = paginationData.totalItems;
          this.currentPage = paginationData.currentPage;
          this.hasNextPage = paginationData.hasNextPage;
          this.hasPreviousPage = paginationData.hasPreviousPage;
          this.isLoading = false;
          console.log('✅ Episodes loaded successfully:', this.episodes.length, 'episodes');
        },
        error: (err) => {
          console.error('❌ Errore nel caricamento degli episodi:', err);
          this.error = 'Impossibile caricare gli episodi. Riprova più tardi.';
          this.isLoading = false;
        }
      });
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { page },
      queryParamsHandling: 'merge'
    });
  }

  /**
   * Mappa i dati dal formato backend (snake_case) al formato frontend (camelCase)
   */
  private mapEpisodesFromBackend(backendData: any[]): EpisodeListItem[] {
    console.log('📊 Raw backend data:', backendData[0]); // Debug: vediamo cosa arriva dal backend
    
    return backendData.map(episode => {
      // Parse tag_names se è una stringa JSON
      let tagNames: string[] = [];
      let tagSlugs: string[] = [];
      
      if (episode.tag_names) {
        if (typeof episode.tag_names === 'string') {
          try {
            const parsed = JSON.parse(episode.tag_names);
            tagNames = Array.isArray(parsed) ? parsed.filter((t: any) => t !== null) : [];
          } catch {
            tagNames = [];
          }
        } else if (Array.isArray(episode.tag_names)) {
          tagNames = episode.tag_names.filter((t: any) => t !== null);
        }
      }
      
      if (episode.tag_slugs) {
        if (typeof episode.tag_slugs === 'string') {
          try {
            const parsed = JSON.parse(episode.tag_slugs);
            tagSlugs = Array.isArray(parsed) ? parsed.filter((t: any) => t !== null) : [];
          } catch {
            tagSlugs = [];
          }
        } else if (Array.isArray(episode.tag_slugs)) {
          tagSlugs = episode.tag_slugs.filter((t: any) => t !== null);
        }
      }

      return {
        id: episode.id,
        title: episode.title,
        slug: episode.slug,
        description: episode.description,
        duration: episode.audio_duration || 0,
        publishedAt: new Date(episode.published_at),
        coverImage: episode.cover_image,
        categoryName: episode.category_name || 'Uncategorized',
        categorySlug: episode.category_slug,
        tagNames: tagNames,
        tagSlugs: tagSlugs
      };
    });
  }

  /**
   * Ottiene l'URL completo dell'immagine di copertina
   */
  /**
   * Ottiene l'URL completo dell'immagine di copertina
   * @param coverImage Nome file immagine
   * @param size Dimensione thumbnail: 'small' | 'medium' | 'large' | undefined (original)
   */
  getCoverImageUrl(coverImage: string | undefined, size?: 'small' | 'medium' | 'large'): string {
    if (!coverImage) {
      if (this.defaultCoverUrl) {
        return this.defaultCoverUrl;
      }
      return 'https://via.placeholder.com/800x450/667eea/ffffff?text=PodLab';
    }
    if (coverImage.startsWith('http')) {
      return coverImage;
    }
    
    // Costruisci l'URL base rimuovendo /api dalla fine
    let baseUrl = environment.apiUrl;
    if (baseUrl.endsWith('/api')) {
      baseUrl = baseUrl.slice(0, -4); // Rimuove '/api'
    }
    
    // Se richiede un thumbnail specifico
    if (size) {
      const ext = coverImage.substring(coverImage.lastIndexOf('.'));
      const nameWithoutExt = coverImage.substring(0, coverImage.lastIndexOf('.'));
      const thumbnailPath = `thumbs/${nameWithoutExt}-${size}${ext}`;
      
      if (thumbnailPath.startsWith('/')) {
        return `${baseUrl}${thumbnailPath}`;
      }
      return `${baseUrl}/media/covers/${thumbnailPath}`;
    }
    
    // Altrimenti usa l'originale
    if (coverImage.startsWith('/')) {
      return `${baseUrl}${coverImage}`;
    }
    return `${baseUrl}/media/covers/${coverImage}`;
  }

  formatDuration(seconds: number): string {
    if (!seconds || isNaN(seconds)) {
      return '0:00';
    }
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  formatDate(date: Date | string): string {
    if (!date) {
      return 'Data non disponibile';
    }
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) {
      return 'Data non disponibile';
    }
    return dateObj.toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  get pageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    
    let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(this.totalPages, start + maxVisible - 1);
    
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    return pages;
  }
}
